import React, { useState, useEffect, useCallback } from 'react'
import { Button, TextBox} from '@ds/ui';
import { apiFetch } from '../../utils/apiClient';

export default function ApplianceNameComponent() {
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [originalValue, setOriginalValue] = useState(inputValue);
  const [isSuccess, setIsSuccess] = useState(false);

  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch('Admin/Home/GetApplianceName');
      const data = await response.json();
      setInputValue(data.applianceName);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const newValue = e.target.value;
    // Only allow alphanumeric characters and underscores
    if (newValue === '' || /^[a-zA-Z0-9_]+$/.test(newValue)) {
      setInputValue(newValue);
      setError('');
    } else {
      setError('Only letters, numbers, and underscores are allowed');
    }
  };

  const handleUpdate = useCallback(async () => {
    if (!inputValue.match(/^[a-zA-Z0-9_]+$/)) {
      setError('Only letters, numbers, and underscores are allowed');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiFetch('Admin/Home/UpdateName', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: inputValue })
      });
      
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      
      setInputValue(data.applianceName);
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000); // Reset after 5 seconds
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [inputValue]);

  const handleFocus = () => {
    setIsEditing(true);
    setOriginalValue(inputValue);
  };

  const handleBlur = () => {
    if (inputValue === originalValue) {
      setIsEditing(false);
    }
  };

  return (
    <div style={{ 
      width: '36%',
    }}>
      <TextBox
        label=""
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={isLoading}
        style={{ width: 'auto' }}
        error={error}
        endElement={
          <Button 
            onClick={handleUpdate} 
            accessibilityText="Update Appliance Name" 
            icon={isLoading ? "processSpinner" : (isSuccess ? "check" : (isEditing ? "arrowRight" : "pencil"))}
            kind={isSuccess ? "primary" : "tertiary"}
            size="small"
          />
        }
      />
    </div>
  );
}
